import React, {useContext, useEffect, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {DeliveryWindowConfigContext} from "./context/DeliveryWindowConfigContext";
import Checkbox from "@amzn/meridian/checkbox";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import TimePicker from "@amzn/meridian/time-picker"
import {getWindowString} from "./Utils";
import Alert from "@amzn/meridian/alert";
import {DEFAULT_TIME_WINDOW, DELIVERY_WINDOW_CONFIG_ALERTS} from "./Constants";
import Loader from "../loader/Loader";

const DeliveryWindowConfigTable = ({intl}) => {
    const {viewModel, dispatch} = useContext(DeliveryWindowConfigContext)
    const [edit, setEdit] = useState(false);
    const [editTimeWindowString, setTimeWindowString] = useState(DEFAULT_TIME_WINDOW);

    const loadViewModel = () => {
        dispatch({
            type: 'LOAD_VIEW',
            data: {dispatcher: dispatch}
        })
    }

    const updateCheckbox = (checked, window, cycleName) => {
        if (checked) {
            dispatch({
                type: 'ADD_CYCLE',
                data: {windowString: getWindowString(window), cycleName: cycleName}
            })
        }
        else {
            dispatch({
                type: 'REMOVE_CYCLE',
                data: {windowString: getWindowString(window), cycleName: cycleName}
            })
        }
    }

    const saveConfig = () => {
        dispatch({type: 'SAVE_CONFIG', data: {dispatch: dispatch}})
        setEdit(false)
    }

    const isChecked = (cycleName,cycleList) => {
        return cycleList.includes(cycleName);
    }

    const isValidWindow = (window) => {
        return window.startTimeString.length > 0 && window.endTimeString.length > 0
    }

    const isNewWindow = (window) => {
        let windowString = getWindowString(window)
        return !viewModel.packageCycleConfig.hasOwnProperty(windowString);
    }

    const addTimeWindow = () => {
        if (isValidWindow(editTimeWindowString)) {
            const timeWindow = {startTime: {hour: {}, minute: {}}, endTime: {hour: {}, minute: {}}}
            timeWindow.startTime.hour = parseInt(editTimeWindowString.startTimeString.slice(0, 2), 10)
            timeWindow.startTime.minute = parseInt(editTimeWindowString.startTimeString.slice(3, 5), 10)
            timeWindow.endTime.hour = parseInt(editTimeWindowString.endTimeString.slice(0, 2), 10)
            timeWindow.endTime.minute = parseInt(editTimeWindowString.endTimeString.slice(3, 5), 10)
            if (isNewWindow(timeWindow)) {
                dispatch({type: 'ADD_WINDOW', data: {window: timeWindow}})
                setTimeWindowString(DEFAULT_TIME_WINDOW)
            }
        }
    }

    const removeTimeWindow = (window) => {
        dispatch({
            type: 'REMOVE_WINDOW',
            data: {windowString: getWindowString(window)}
        })
    }

    useEffect(() => {
        loadViewModel();
    }, []);

    const renderAlert = (alert) => {
        return (alert.id !== "" ?
            <Alert size="medium" type={alert.type} onClose={() => dispatch({type: "CLEAR_ALERT"})}>
                <FormattedMessage id={alert.id} defaultMessage={alert.default} values={alert.values}/>
            </Alert> : null)
    }

    const renderStaticAlert = (alert) => {
        return (alert.id !== "" ?
            <Alert size="medium" type={alert.type}>
                <FormattedMessage id={alert.id} defaultMessage={alert.default} values={alert.values}/>
            </Alert> : null)
    }

    const renderTableHeaders = () => {
        return (
            <TableRow>
                <TableCell alignmentHorizontal={"left"} width={"200px"}> <b>
                    <FormattedMessage id={"inductmanagement.deliveryWindow"} defaultMessage={"Delivery Window"}/>
                </b> </TableCell>
                <TableCell alignmentHorizontal={"center"} columnSpan={12}> <b>
                    <FormattedMessage id={"inductmanagement.cycles"} defaultMessage={"Cycles"}/>
                </b> </TableCell>
            </TableRow>
            )
    }

    const renderCycleNames = () => {
        return (
            <TableRow>
                <TableCell/>
                {viewModel.cycleList.map((cycleName) =>
                    <TableCell alignmentHorizontal={"center"}>
                        {cycleName}
                    </TableCell>
                )}
            </TableRow>
        )
    }

    const renderTableRows = () => {
        return (
            Object.values(viewModel.packageCycleConfig).map(({window, cycleList}) =>
                <TableRow>
                    <TableCell width={'grid-1'}>
                        {edit && <Button onClick={()=>removeTimeWindow(window)} type="link"> X </Button>}
                        {getWindowString(window)}
                    </TableCell>
                    {viewModel.cycleList.map((cycleName) =>
                        <TableCell> <Column alignmentHorizontal={"center"}>
                            <Checkbox alignmentHorizontal="center"
                                      checked={isChecked(cycleName,cycleList)}
                                      onChange={(checked) => updateCheckbox(checked, window, cycleName)}
                                      disabled={!edit} />
                        </Column> </TableCell>
                    )}
                </TableRow>
            )
        )
    }

    const renderAddWindow = () => {
        return (
            <Row>
                <TimePicker
                    showIcon={false}
                    width={150}
                    label={intl.formatMessage({id:"inductmanagement.start", default: "Start"})}
                    value={editTimeWindowString.startTimeString}
                    onChange={(value)=>setTimeWindowString({...editTimeWindowString, startTimeString: value})}
                    format={{ hourCycle: "h23", hour: "2-digit", minute: "2-digit" }}/>
                <TimePicker
                    showIcon={false}
                    width={150}
                    label={intl.formatMessage({id:"inductmanagement.end", default: "End"})}
                    value={editTimeWindowString.endTimeString}
                    onChange={(value)=>setTimeWindowString({...editTimeWindowString, endTimeString: value})}
                    format={{ hourCycle: "h23", hour: "2-digit", minute: "2-digit" }}/>
                <Button onClick={addTimeWindow}>
                    <FormattedMessage id={"inductmanagement.addNewWindow"} defaultMessage={"Add new window"}/>
                </Button>
            </Row>
        )
    }

    if (viewModel.loadingCycle || viewModel.loadingConfig) {
        return (<Loader/>)
    }
    if (viewModel.isNodeEnabled === false) {
        return renderStaticAlert(DELIVERY_WINDOW_CONFIG_ALERTS.NOT_ENABLED)
    }
    if (viewModel.cycleList.length === 0) {
        return renderStaticAlert(DELIVERY_WINDOW_CONFIG_ALERTS.NO_CYCLES)
    }
    else {
        return (
            <Column>
                {renderAlert(viewModel.alert)}
                <Row alignmentHorizontal={"right"}>
                    { edit && <Button size={"small"} onClick={saveConfig}>
                        <FormattedMessage id={"inductmanagement.save"} defaultMessage={"Save"}/>
                    </Button>}
                    <Button size={"small"}
                            onClick={()=>setEdit(true)} disabled={!viewModel.isEditable ||  edit}>
                        <FormattedMessage id={"inductmanagement.edit"} defaultMessage={"Edit"}/>
                    </Button>
                </Row>
                <div style={{ overflowX: "auto", width: "100%" }}>
                    <Table headerRows={2}
                           showStripes={false}
                           showDividers={false}
                           headerColumns={1}
                           fixHeaderRows = {true}
                           fixHeaderRowsFullWidth={true}
                           stickyHeaderColumn={true}>
                        {renderTableHeaders()}
                        {viewModel.cycleList.length > 0 && renderCycleNames()}
                        {Object.keys(viewModel.packageCycleConfig).length > 0 && viewModel.cycleList.length > 0 &&
                        renderTableRows()}
                    </Table>
                </div>
                {Object.keys(viewModel.packageCycleConfig).length === 0 &&
                !edit && renderStaticAlert(DELIVERY_WINDOW_CONFIG_ALERTS.NO_WINDOWS)}
                {edit && renderAddWindow()}
            </Column>
        )
    }

}

export default injectIntl(DeliveryWindowConfigTable);


// WEBPACK FOOTER //
// ./src/components/inductmanagementv2/DeliveryWindowConfigTable.js